import { FilterPreferenceToggle, FiltersPreferences } from '@if/mui'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SaveIcon from '@mui/icons-material/Save'
import { Alert, Snackbar, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButtonWithTooltip from './IconButtonWithTooltip'

interface FilterPreferenceActionBarProps {
  preferences: FiltersPreferences
  /**
   * The same `preferencesEnabledKey` passed to `useUrlFilters` / `useUrlFiltersAndPagination`.
   * Wires the toggle through `usePreferenceEnabled` so all consumers of the same key stay in
   * sync without prop-drilling.
   */
  preferenceKey: string
}

export const FilterPreferenceActionBar = ({
  preferences,
  preferenceKey,
}: FilterPreferenceActionBarProps) => {
  const { t } = useTranslation()
  const [savedSnackbarOpen, setSavedSnackbarOpen] = useState(false)

  const handleSavePreference = () => {
    preferences.save()
    setSavedSnackbarOpen(true)
  }

  return (
    <>
      <Stack>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <IconButtonWithTooltip
            tooltip={t('common.restoreFilterPreference')}
            icon={<RestartAltIcon />}
            onClick={() => preferences.restore()}
            disabled={preferences.matches || !preferences.enabled}
          />
          <IconButtonWithTooltip
            tooltip={t('common.saveFilterPreference')}
            icon={<SaveIcon />}
            onClick={handleSavePreference}
            disabled={preferences.matches}
          />
          <FilterPreferenceToggle
            preferenceKey={preferenceKey}
            enabledLabel={t('common.filterPreferenceActive')}
            disabledLabel={t('common.filterPreferenceInactive')}
          />
        </Stack>
        <Typography variant="caption" color="textSecondary">
          {preferences.enabled ? t('common.filterPreferenceActive') : t('common.filterPreferenceInactive')}
        </Typography>
      </Stack>
      <Snackbar
        open={savedSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSavedSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSavedSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('common.filterPreferenceSaved')}
        </Alert>
      </Snackbar>
    </>
  )
}
